'use client';

/**
 * /funnels — venture funnel cockpit (Phase 5.A).
 * Registry-driven: every funnel provider registered in funnels.db shows up in
 * the picker. Data flows provider API → snapshot (funnels.db) → this page.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Filter, Loader2, RefreshCw, Users, CalendarCheck, PlayCircle, DollarSign,
  TrendingDown, FlaskConical, Mail, ChevronDown, WifiOff,
} from 'lucide-react';
import {
  ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import { VideoAnalyzer, type VideoStats } from '@/components/funnels/VideoAnalyzer';
import { AbSplitView } from '@/components/funnels/AbSplitView';

// ---------- types (mirror the provider payloads) ----------

interface FunnelInfo { id: string; name: string; baseUrl?: string; lastSync: string | null; keyConfigured: boolean }
interface StatsResponse {
  funnel: { id: string; name: string };
  stats: Record<string, any>;
  meta: Record<string, { fetchedAt: string; live: boolean; error?: string }>;
}

const RANGES = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: 'All', days: 0 },
];

// ---------- small building blocks ----------

function Kpi({ icon: Icon, label, value, sub, color }: {
  icon: any; label: string; value: string | number; sub?: string; color: string;
}) {
  return (
    <div className="glass-card px-4 py-3.5">
      <div className="flex items-center gap-2 mb-1.5">
        <Icon className={`w-3.5 h-3.5 ${color}`} />
        <p className="text-[10px] text-gray-500 uppercase tracking-[0.14em]">{label}</p>
      </div>
      <span className={`text-3xl font-bold tracking-tight tabular-nums ${color}`}>{value}</span>
      {sub && <p className="text-[11px] text-gray-500 mt-0.5">{sub}</p>}
    </div>
  );
}

function Panel({ title, icon: Icon, children, note, action }: {
  title: string; icon: any; children: React.ReactNode; note?: string; action?: React.ReactNode;
}) {
  return (
    <div className="glass-card p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-cyan-400" />
          <h2 className="text-sm font-semibold text-white">{title}</h2>
        </div>
        <div className="flex items-center gap-3">
          {note && <span className="text-[10px] text-gray-500">{note}</span>}
          {action}
        </div>
      </div>
      {children}
    </div>
  );
}

/**
 * A conversion rate can never exceed 100% — more converters than arrivals is
 * a tracking bug (event overcount / untracked entries). Never display it as a
 * real number.
 */
function rateOk(r: number | null | undefined): boolean {
  return typeof r === 'number' && isFinite(r) && r >= 0 && r <= 100;
}

/** 5-stage funnel visualization: proportional bars + stage-to-stage conversion. */
function FunnelStages({ stages }: { stages: any[] }) {
  if (!stages?.length) return <p className="text-xs text-gray-500">No funnel data.</p>;
  const max = Math.max(...stages.map(s => s.count), 1);
  return (
    <div className="space-y-2">
      {stages.map((s, i) => (
        <div key={s.key}>
          {i > 0 && (
            <p className="text-[10px] text-gray-500 pl-1 mb-0.5">
              {rateOk(s.conversionFromPrevious)
                ? <>↓ {s.conversionFromPrevious}% from previous</>
                : <span className="text-amber-400/80" title="More people counted at this stage than the previous one — event overcount or untracked entries. Not a real rate.">⚠ tracking error ({s.conversionFromPrevious}%)</span>}
            </p>
          )}
          <div className="flex items-center gap-3">
            <div className="w-36 text-xs text-gray-400 truncate" title={s.label}>{s.label}</div>
            <div className="flex-1 h-6 rounded bg-white/5 overflow-hidden">
              <div
                className="h-full rounded bg-gradient-to-r from-cyan-500/70 to-blue-500/70 flex items-center px-2"
                style={{ width: `${Math.max((s.count / max) * 100, 4)}%` }}
              >
                <span className="text-[11px] font-semibold text-white whitespace-nowrap">{s.count.toLocaleString()}</span>
              </div>
            </div>
            <div className="w-14 text-right text-[11px] text-gray-500">{rateOk(s.conversionFromStart) ? `${s.conversionFromStart}%` : '—'}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------- page ----------

export default function FunnelsPage() {
  const [funnels, setFunnels] = useState<FunnelInfo[]>([]);
  const [funnelId, setFunnelId] = useState('');
  const [days, setDays] = useState(30);
  const [data, setData] = useState<StatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [analyzeVideo, setAnalyzeVideo] = useState<VideoStats | null>(null);

  useEffect(() => {
    fetch('/api/funnels')
      .then(r => r.json())
      .then(d => {
        const fs: FunnelInfo[] = d.funnels || [];
        setFunnels(fs);
        // deep-link from project suite: /funnels?funnel=<id>
        const wanted = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('funnel') : null;
        if (wanted && fs.some(f => f.id === wanted)) setFunnelId(wanted);
        else if (fs.length) setFunnelId(fs[0].id);
      })
      .catch(e => toast.error(e.message));
  }, []);

  const load = useCallback((id: string, d: number) => {
    if (!id) return;
    setLoading(true);
    const qs = d ? `?days=${d}` : '';
    fetch(`/api/funnels/${id}/stats${qs}`)
      .then(async r => {
        const body = await r.json();
        if (body.error) throw new Error(body.error);
        setData(body);
        const stale = Object.values(body.meta || {}).filter((m: any) => !m.live);
        if (stale.length) toast.warning(`${stale.length} panel(s) served from cache — provider unreachable`);
      })
      .catch(e => toast.error(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(funnelId, days); }, [funnelId, days, load]);

  // LIVE STATS (Boss 2026-07-07: "every stat updated all the time — I walk
  // and see the newest data"): silent refetch every 60s while the tab is
  // visible. No spinner — numbers just stay current.
  useEffect(() => {
    const iv = setInterval(() => {
      if (document.hidden || !funnelId) return;
      const qs = days ? `?days=${days}` : '';
      fetch(`/api/funnels/${funnelId}/stats${qs}`)
        .then(r => r.json())
        .then(body => { if (!body.error) setData(body); })
        .catch(() => {});
    }, 60_000);
    return () => clearInterval(iv);
  }, [funnelId, days]);

  const s = data?.stats || {};
  const overview = s.overview;
  const anyCached = Object.values(data?.meta || {}).some(m => !m.live);

  // merged daily series: traffic pageviews + new leads
  const daily: Array<{ date: string; traffic: number; leads: number }> = (() => {
    const map = new Map<string, { date: string; traffic: number; leads: number }>();
    for (const t of s.traffic?.daily || []) map.set(t.date, { date: t.date, traffic: t.count, leads: 0 });
    for (const l of s.leads?.daily || []) {
      const e = map.get(l.date) || { date: l.date, traffic: 0, leads: 0 };
      e.leads = l.count; map.set(l.date, e);
    }
    return [...map.values()].sort((a, b) => a.date.localeCompare(b.date));
  })();

  return (
    <div className="p-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600">
            <Filter className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Funnels</h1>
            <p className="text-xs text-gray-500">Every venture funnel, one cockpit. Raw stats snapshotted to funnels.db.</p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {anyCached && (
            <span className="flex items-center gap-1 text-[11px] text-amber-400"><WifiOff className="w-3.5 h-3.5" /> cached</span>
          )}
          <div className="relative">
            <select
              value={funnelId}
              onChange={e => setFunnelId(e.target.value)}
              className="appearance-none bg-[#0d1117]/85 border border-white/[0.08] rounded-lg pl-3 pr-9 py-2 text-sm text-white focus:outline-none focus:border-cyan-500/50 cursor-pointer"
            >
              {funnels.map(f => <option key={f.id} value={f.id}>{f.name}{f.keyConfigured ? '' : ' (no key)'}</option>)}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
          </div>
          <div className="flex rounded-lg border border-white/[0.08] overflow-hidden">
            {RANGES.map(r => (
              <button
                key={r.label}
                onClick={() => setDays(r.days)}
                className={`px-3 py-2 text-xs transition-colors ${
                  days === r.days ? 'bg-cyan-500/15 text-cyan-300' : 'bg-[#0d1117]/85 text-gray-400 hover:text-white'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => load(funnelId, days)}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-white/[0.08] bg-white/[0.05] text-sm text-gray-300 hover:text-white hover:border-cyan-500/40 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {loading && !data && (
        <div className="flex items-center justify-center h-72">
          <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
        </div>
      )}

      {data && (
        <div className="space-y-5">
          {/* KPIs (overview is all-time + windows by design) */}
          {overview && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Kpi icon={Users} label="Leads" value={overview.leads?.total ?? '—'}
                sub={`${overview.leads?.last7Days ?? 0} last 7d · ${overview.leads?.today ?? 0} today`} color="text-cyan-300" />
              <Kpi icon={CalendarCheck} label="Booked calls" value={overview.appointments?.booked ?? '—'}
                sub={`${overview.appointments?.cancellationRate ?? 0}% cancel rate`} color="text-green-300" />
              <Kpi icon={PlayCircle} label="Video views" value={overview.videoViews?.total ?? '—'}
                sub={`${overview.videoViews?.last7Days ?? 0} last 7d`} color="text-blue-300" />
              {overview.revenue?.gross ? (
                <Kpi icon={DollarSign} label="Revenue" value={`$${overview.revenue.gross.toLocaleString()}`}
                  sub={`${overview.revenue?.orders ?? 0} orders`} color="text-amber-300" />
              ) : (
                <Kpi icon={TrendingDown} label="Conversion" value={`${overview.conversion?.visitorToBookedRate ?? 0}%`}
                  sub={`${overview.conversion?.visitorToLeadRate ?? 0}% visitor→lead · ${overview.conversion?.leadToBookedRate ?? 0}% lead→booked`}
                  color="text-amber-300" />
              )}
            </div>
          )}

          {/* Funnel + traffic chart */}
          <div className="grid lg:grid-cols-2 gap-5">
            <Panel title="Acquisition funnel" icon={TrendingDown} note={days ? `last ${days}d` : 'all time'}>
              <FunnelStages stages={s.funnel?.stages || []} />
            </Panel>

            <Panel title="Traffic & new leads" icon={Users} note={days ? `last ${days}d` : 'all time'}>
              {daily.length ? (
                <ResponsiveContainer width="100%" height={230}>
                  <ComposedChart data={daily}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                    <XAxis dataKey="date" tick={{ fill: '#6b7280', fontSize: 10 }} tickFormatter={d => d.slice(5)} />
                    <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} width={30} />
                    <Tooltip contentStyle={{ background: '#0b1220', border: '1px solid #1f2937', fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Area type="monotone" dataKey="traffic" name="Sessions" stroke="#22d3ee" fill="#22d3ee22" strokeWidth={1.5} />
                    <Line type="monotone" dataKey="leads" name="New leads" stroke="#4ade80" strokeWidth={2} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : <p className="text-xs text-gray-500">No daily series in range.</p>}
            </Panel>
          </div>

          {/* Videos — click a row to open the in-Beast analyzer */}
          {analyzeVideo && (
            <VideoAnalyzer
              funnelId={funnelId}
              video={analyzeVideo}
              hlsOrigin={new URL(funnels.find(f => f.id === funnelId)?.baseUrl || 'https://yevlakhova.com').origin}
              onClose={() => setAnalyzeVideo(null)}
            />
          )}
          <Panel title="Video engagement" icon={PlayCircle}
            note={s.videos?.totals ? `${s.videos.totals.totalViews} views · ${s.videos.totals.completionRate}% completed · click a video to analyze` : undefined}
            action={<a href="/funnels/video-lab" className="text-xs rounded-lg px-3 py-1.5 bg-violet-500/15 text-violet-300 border border-violet-500/25 hover:bg-violet-500/25 transition-colors">Video Lab — recut & re-voice</a>}>
            {s.videos?.videos?.length ? (
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-gray-500 text-left border-b border-white/[0.04]">
                    <th className="py-2 font-medium">Video</th>
                    <th className="py-2 font-medium text-right">Views</th>
                    <th className="py-2 font-medium text-right">Unique</th>
                    <th className="py-2 font-medium text-right">Avg completion</th>
                    <th className="py-2 font-medium text-right">Completion rate</th>
                    <th className="py-2 font-medium text-right">Avg stop (s)</th>
                  </tr>
                </thead>
                <tbody>
                  {s.videos.videos.map((v: any) => (
                    <tr key={`${v.videoPage}/${v.videoId}`}
                      onClick={() => setAnalyzeVideo(v)}
                      className={`border-b border-white/[0.04] cursor-pointer transition-colors ${
                        analyzeVideo?.videoId === v.videoId ? 'bg-cyan-500/10 text-cyan-200' : 'text-gray-300 hover:bg-white/5'
                      }`}>
                      <td className="py-2"><PlayCircle className="w-3.5 h-3.5 inline mr-1.5 text-cyan-400" />{v.videoId} <span className="text-gray-600">on /{v.videoPage}</span></td>
                      <td className="py-2 text-right">{v.totalViews}</td>
                      <td className="py-2 text-right">{v.uniqueViewers}</td>
                      <td className="py-2 text-right">{v.avgCompletion}%</td>
                      <td className="py-2 text-right">{v.completionRate}%</td>
                      <td className="py-2 text-right">{Math.round(v.avgStopPosition ?? 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <p className="text-xs text-gray-500">No video data.</p>}
          </Panel>

          {/* A/B tests — full-width live split view */}
          <Panel title="A/B tests — live split" icon={FlaskConical}>
            {s['ab-tests']?.tests?.length ? (
              <AbSplitView
                funnelId={funnelId}
                origin={new URL(funnels.find(f => f.id === funnelId)?.baseUrl || 'https://yevlakhova.com').origin}
                tests={s['ab-tests'].tests}
              />
            ) : <p className="text-xs text-gray-500">No A/B tests.</p>}
          </Panel>

          <div className="grid lg:grid-cols-2 gap-5">
            <Panel title="Email & appointments" icon={Mail} note={days ? `last ${days}d` : 'all time'}>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="rounded bg-white/5 px-3 py-2.5">
                  <p className="text-gray-500 mb-1">Email reminders</p>
                  <p className="text-lg font-bold text-white">{s.email?.emailReminders?.total ?? 0}</p>
                  <p className="text-[10px] text-gray-500">
                    {(s.email?.emailReminders?.byStatus || []).map((b: any) => `${b.count} ${b.value}`).join(' · ') || '—'}
                  </p>
                </div>
                <div className="rounded bg-white/5 px-3 py-2.5">
                  <p className="text-gray-500 mb-1">Call reminders</p>
                  <p className="text-lg font-bold text-white">{s.email?.callReminders?.total ?? 0}</p>
                  <p className="text-[10px] text-gray-500">
                    {(s.email?.callReminders?.byType || []).map((b: any) => `${b.count} ${b.value}`).join(' · ') || '—'}
                  </p>
                </div>
                <div className="rounded bg-white/5 px-3 py-2.5">
                  <p className="text-gray-500 mb-1">Appointments in range</p>
                  <p className="text-lg font-bold text-white">{s.appointments?.inRange?.total ?? 0}</p>
                  <p className="text-[10px] text-gray-500">{s.appointments?.upcoming ?? 0} upcoming · {s.appointments?.inRange?.cancellationRate ?? 0}% cancelled</p>
                </div>
                <div className="rounded bg-white/5 px-3 py-2.5">
                  <p className="text-gray-500 mb-1">Unsubscribes</p>
                  <p className="text-lg font-bold text-white">{s.email?.preferences?.unsubscribedAll ?? 0}</p>
                  <p className="text-[10px] text-gray-500">of {s.email?.preferences?.total ?? 0} preference rows</p>
                </div>
              </div>
            </Panel>
          </div>
        </div>
      )}
    </div>
  );
}
